import { useEffect } from "react"

import { useQueries, useQuery } from "@tanstack/react-query"

import { l2rApiAxiosInstance as axiosInstance } from "../constants/apis"
import { STATUS_CODE_UNAUTHORIZED } from "../constants/httpStatus"
import { useAlertsDispatchContext } from "../contexts/AlertsContext"

const REFETCH_INTERVAL = 7 * 60 * 1000

export async function queryFn(
    axiosInstance,
    url,
    queryParameters,
    config,
) {
    const response = await axiosInstance.get(url, {
        params: { ...queryParameters },
        ...config,
    })
    return response.data
}

export function useL2rQuery(url, queryKey, queryParameters, config = {}, initialData) {
    const {
        autoRefetch = false,
        fetchConfig,
        onError,
        errorSnackbarMessage,
        ...queryConfig
    } = (config || {})

    const { setSnackbar } = useAlertsDispatchContext()

    const result = useQuery({
        queryKey,
        queryFn: () => queryFn(axiosInstance, url, queryParameters, fetchConfig),
        ...queryConfig,
        ...(autoRefetch && {
            refetchInterval: REFETCH_INTERVAL,
            refetchIntervalInBackground: true,
            staleTime: REFETCH_INTERVAL,
        }),
        initialData,
    })

    useEffect(() => {
        if (result.isError && result?.error?.status !== STATUS_CODE_UNAUTHORIZED) {
            onError?.()
            if (errorSnackbarMessage) {
                setSnackbar(errorSnackbarMessage)
            }
        }
    }, [result.isError, result?.error?.status, onError, errorSnackbarMessage, setSnackbar])

    return result
}

export function useL2rQueries(queriesData) {
    const queries = queriesData?.map(queryData => {
        const {
            fetchConfig,
            ...queryConfig
        } = (queryData.config || {})
        return {
            queryKey: queryData.queryKey,
            queryFn: () => queryFn(axiosInstance, queryData.url, queryData.queryParameters, fetchConfig),
            ...queryConfig,
        }
    })

    return useQueries({ queries: queries || [] })
}